import React from 'react';
import {
    Grid,
    Typography,
    Box,
    Card,
    CardContent,
    Container,
    useTheme,
    useMediaQuery
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import HomeIcon from '@material-ui/icons/Home';
import Layout from '../components/Layout';
import tata_logo from '../Assets/tata_logo.png';
import config from '../config/config';
import { IconButton, SubHeader } from '@tmlconnected/avant-garde-components-library';

const useStyles = makeStyles((theme) => ({
    homeContainer: {
        fontFamily: '"Segoe UI", sans-serif',
        backgroundColor: '#f9f9f9',
        minHeight: '100vh',
        paddingTop: theme.spacing(2),
        paddingBottom: theme.spacing(4),
        textTransform: "none"
    },
    logoContainer: {
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        marginBottom: theme.spacing(3),
        padding: theme.spacing(2),
        backgroundColor: '#ffffff',
        borderRadius: theme.spacing(1),
        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
    },
    logo: {
        maxWidth: '100%',
        height: 'auto',
        [theme.breakpoints.down('sm')]: {
            maxWidth: '300px',
        },
    },
    sectionCard: {
        backgroundColor: '#ffffff',
        borderRadius: theme.spacing(1),
        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
        marginBottom: theme.spacing(3),
        transition: 'transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out',
        '&:hover': {
            transform: 'translateY(-2px)',
            boxShadow: '0 6px 20px rgba(0, 0, 0, 0.15)',
        },
    },
    sectionHeader: {
        fontSize: '1.5rem',
        fontWeight: 'bold',
        color: '#093b64',
        marginBottom: theme.spacing(2),
        position: 'relative',
        paddingLeft: theme.spacing(2),
        '&::before': {
            content: '""',
            position: 'absolute',
            left: 0,
            top: 0,
            bottom: 0,
            width: '5px',
            backgroundColor: '#007bff',
            borderRadius: '2px',
        },
        [theme.breakpoints.down('sm')]: {
            fontSize: '1.25rem',
        },
    },
    aboutText: {
        fontSize: '1rem',
        lineHeight: 1.6,
        color: '#1f2d3d',
        marginBottom: theme.spacing(1.5),
        textAlign: 'justify',
    },
    featureItem: {
        backgroundColor: '#ffffff',
        padding: theme.spacing(2),
        marginBottom: theme.spacing(1.5),
        borderRadius: theme.spacing(1),
        borderLeft: '5px solid #007bff',
        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
        transition: 'all 0.2s ease-in-out',
        '&:hover': {
            transform: 'translateX(4px)',
            boxShadow: '0 4px 16px rgba(0, 0, 0, 0.12)',
            borderLeftColor: '#0056b3',
        },
        '&:last-child': {
            marginBottom: 0,
        },
    },
    featureTitle: {
        fontWeight: 'bold',
        color: '#1d3557',
        marginBottom: theme.spacing(1),
        fontSize: '1rem',
        [theme.breakpoints.down('sm')]: {
            fontSize: '0.9rem',
        },
    },
    featureDescription: {
        fontSize: '0.95rem',
        lineHeight: 1.6,
        color: '#1f2d3d',
        [theme.breakpoints.down('sm')]: {
            fontSize: '0.85rem',
        },
    },
    divider: {
        margin: theme.spacing(2, 0),
        backgroundColor: '#e0e0e0',
    },
    subHeaderContainer: {
        marginBottom: theme.spacing(3),
        '& .MuiGrid-item': {
            display: 'flex',
            justifyContent: 'center',
        },
    },
}));

const HomePage = () => {
    const classes = useStyles();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    // const isTablet = useMediaQuery(theme.breakpoints.down('md'));

    const features = [
        {
            title: "Asset Master Registration & Modification:",
            description: "AMS receives real-time asset data from SAP and RFID Tag numbers from ITMS on a daily basis. It supports bulk upload and modification of asset details, including mapping of RFID Serial Numbers with Asset Codes for accurate tracking."
        },
        {
            title: "Asset Transfer Management:",
            description: "Enables seamless transfer of assets between locations and suppliers, with acceptance workflows for new recipients to ensure transparency and traceability."
        },
        {
            title: "Asset Disposal Process:",
            description: "Facilitates disposal of obsolete tools through a multi-level approval mechanism involving TML's authorized stakeholders."
        },
        {
            title: "Physical Verification via QR/RFID:",
            description: "Physical audits are enhanced with QR code and RFID scanning, along with photo documentation, asset condition tagging, and geo-location capture. It also highlights unidentified assets discovered during scanning."
        },
        {
            title: "Advanced Reporting Capabilities:",
            description: "Generate customizable reports filtered by Asset Code, Part No., Serial No., PO No., SPOC, valuation, depreciation, and capitalization data. Export options available in XLS format."
        },
        {
            title: "Executive Dashboard:",
            description: "Visual summaries and analytics categorized by Supplier, Model, Asset class, and SPOCs for strategic decision-making."
        },
        {
            title: "Preventive Maintenance Scheduling:",
            description: "Create and manage maintenance calendars for individual or multiple assets, minimizing unplanned downtimes."
        },
        {
            title: "Breakdown & Repair Tracking:",
            description: "Log breakdown incidents, share real-time updates, and analyze historical data to identify recurring issues and improve asset reliability."
        },
        {
            title: "Asset Usage Tracker:",
            description: "Monitor tool life with stroke-based tracking. Calculate balance usage by integrating GRN quantity from SAP and supplier-declared rejection data, ensuring proactive alerts for maintenance or replacement."
        },
        {
            title: "Automated Email Notifications:",
            description: "Timely alerts and updates are sent to relevant stakeholders to keep everyone informed and aligned."
        }
    ];

    return (
        <Layout>
            <Box className={classes.homeContainer}>
                <Container maxWidth="lg">
                    {/* Logo Section */}
                    <Grid container spacing={3}>
                        <Grid item xs={12}>
                            <Box className={classes.logoContainer}>
                                <img
                                    src={tata_logo}
                                    alt="Tata Motors Logo"
                                    className={classes.logo}
                                    style={{
                                        width: isMobile ? '300px' : '550px',
                                        height: 'auto'
                                    }}
                                />
                            </Box>
                        </Grid>

                        {/* SubHeader Section */}
                        <Grid item xs={12} className={classes.subHeaderContainer}>
                            <SubHeader
                                config={config}
                                title="Asset Management System"
                                PlantSelectorDropdown={null}
                                HomeIcon={HomeIcon}
                                IconButton={IconButton}
                            />
                        </Grid>

                        {/* About Section */}
                        <Grid item xs={12} md={12}>
                            <Card className={classes.sectionCard}>
                                <CardContent>
                                    <Typography variant="h4" className={classes.sectionHeader}>
                                        About the "Asset Management System (AMS)"
                                    </Typography>
                                    <Typography className={classes.aboutText}>
                                        The Asset Management System (AMS) is an advanced digital platform designed to enhance end-to-end asset lifecycle management.
                                    </Typography>
                                    <Typography className={classes.aboutText}>
                                        Seamlessly integrated with SAP and ITMS, AMS ensures efficient, accurate, and accountable management of tools, dies, and fixtures—from registration through to disposal—providing real-time visibility and control.
                                    </Typography>
                                </CardContent>
                            </Card>
                        </Grid>

                        {/* Features Section */}
                        <Grid item xs={12}>
                            <Card className={classes.sectionCard}>
                                <CardContent>
                                    <Typography variant="h4" className={classes.sectionHeader}>
                                        Key Features:
                                    </Typography>
                                    <Grid container spacing={2}>
                                        {features.map((feature, index) => (
                                            <Grid item xs={12} md={6} key={index}>
                                                <Box className={classes.featureItem}>
                                                    <Typography className={classes.featureTitle}>
                                                        {feature.title}
                                                    </Typography>
                                                    <Typography className={classes.featureDescription}>
                                                        {feature.description}
                                                    </Typography>
                                                </Box>
                                            </Grid>
                                        ))}
                                    </Grid>
                                </CardContent>
                            </Card>
                        </Grid>
                    </Grid>
                </Container>
            </Box>
        </Layout>
    );
};

export default HomePage;